<template>
  <div id="map" ref="leafletMap" class="z-10 h-full w-full" />
</template>

<script setup lang="ts">
import L, { type MapOptions } from 'leaflet'
import 'leaflet.fullscreen'
import iconMap from '/__nuxt-public/images/icon_map.png'
import { useIntersectionObserver } from '@vueuse/core'

interface CustomMapOptions extends MapOptions {
  fullscreenControl?: boolean
}

interface propsInterface {
  latitude: number
  longitude: number
  zoom?: number
}

const props = defineProps<propsInterface>()

const map = ref<L.Map | null>(null)
const leafletMap = ref<HTMLElement | null>(null)
const minZoom = 1
const maxZoom = 19
const iconUrl = iconMap

onMounted(async () => {
  await import('leaflet/dist/leaflet.css')
  await import('leaflet.fullscreen/Control.FullScreen.css')
  await nextTick()

  if (leafletMap.value) {
    let mapLoaded = false
    map.value = L.map(leafletMap.value, {
      center: [props.latitude, props.longitude],
      zoom: props.zoom || 10,
      minZoom,
      maxZoom,
      fullscreenControl: false,
      zoomControl: false,
    } as CustomMapOptions)
    ;(
      L.control as unknown as Record<
        string,
        (opts: Record<string, unknown>) => { addTo: (m: L.Map) => void }
      >
    )
      .zoom({
        forceSeparateButton: false,
        zoomInTitle: t('views.cottage.map.zoom.in'),
        zoomOutTitle: t('views.cottage.map.zoom.out'),
      })
      .addTo(map.value as L.Map)
    ;(
      L.control as unknown as Record<
        string,
        (opts: Record<string, unknown>) => { addTo: (m: L.Map) => void }
      >
    )
      .fullscreen({
        forceSeparateButton: false,
        title: t('views.cottage.map.fullscreen.title'),
        titleCancel: t('views.cottage.map.fullscreen.titleCancel'),
      })
      .addTo(map.value as L.Map)

    useIntersectionObserver(leafletMap, ([entry]) => {
      if (entry?.isIntersecting && !mapLoaded) {
        mapLoaded = true
        L.tileLayer('https://mapas.escapadarural.com/hot/{z}/{x}/{y}.png', {
          maxZoom,
          attribution:
            'Map data: &copy; <a href="http://www.openstreetmap.org/copyright" target="_blank">OpenStreetMap</a>',
        }).addTo(map.value as L.Map)

        const customIcon = L.icon({
          iconUrl,
          iconSize: [30, 38],
        })

        L.marker([props.latitude, props.longitude], {
          icon: customIcon,
        }).addTo(map.value as L.Map)
      }
    })
  }
})
</script>
